import React from 'react';
import {
  Building2,
  ExternalLink,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
} from 'lucide-react';

interface ContactCardProps {
  title: string;
  value: string;
  description: string;
  icon: React.ElementType;
  href?: string;
  testId?: string;
}

const ContactCard: React.FC<ContactCardProps> = ({ title, value, description, icon: Icon, href, testId }) => {
  const body = (
    <>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{title}</p>
        <p className="mt-1 break-words font-semibold text-[var(--text-primary)]">{value}</p>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">{description}</p>
      </div>
      {href && <ExternalLink className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />}
    </>
  );

  if (!href) {
    return (
      <article className="flex items-start gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        {body}
      </article>
    );
  }

  return (
    <a
      href={href}
      data-testid={testId}
      className="group flex items-start gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 transition-colors hover:bg-[var(--gray-50)] sm:p-5"
    >
      {body}
    </a>
  );
};

const SoverdiContactR7: React.FC = () => (
  <div className="space-y-5 pb-24 sm:space-y-6 lg:pb-6" data-testid="client-contact-r7">
    <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="flex items-center gap-2 text-[var(--color-primary)]">
          <MessageCircle className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wide">Aide</span>
        </div>
        <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">Contact</h1>
        <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
          Coordonnées publiques Agri Safe pour une question sur la plateforme, votre installation ou votre accompagnement.
        </p>
      </div>
      <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
        Agri Safe SAS
      </span>
    </section>

    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <ContactCard
        title="E-mail"
        value="contact@agrisafe.fr"
        description="Ouvre votre application de messagerie avec l’adresse Agri Safe."
        icon={Mail}
        href="mailto:contact@agrisafe.fr"
        testId="contact-email"
      />
      <ContactCard
        title="Téléphone"
        value="+33 6 95 46 43 39"
        description="Appel direct depuis un appareil compatible."
        icon={Phone}
        href="tel:+33695464339"
        testId="contact-phone"
      />
      <ContactCard
        title="Adresse"
        value="31 Allées Jules Guesde, Le Village by CA, Toulouse"
        description="Siège et implantation Agri Safe à Toulouse."
        icon={MapPin}
      />
      <ContactCard
        title="Site web"
        value="www.agrisafe.fr"
        description="Présentation publique des solutions et ressources Agri Safe."
        icon={Building2}
        href="https://www.agrisafe.fr/"
        testId="contact-website"
      />
    </div>

    <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
      <h2 className="font-semibold text-[var(--text-primary)]">Pour accélérer le traitement d’une demande</h2>
      <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">
        Indiquez le nom de l’exploitation, le site concerné, l’écran utilisé et ce que vous observez. N’envoyez pas de mot de passe, clé AWS, secret TTN ou identifiant technique sensible par e-mail.
      </p>
    </section>

    <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 text-sm text-[var(--text-secondary)] sm:p-5">
      Cette page ne transmet aucune donnée automatiquement. Les liens e-mail, téléphone et site web ouvrent simplement l’application correspondante sur votre appareil.
    </section>
  </div>
);

export default SoverdiContactR7;
